import { useState } from 'react'
import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import { Button, Input } from '@/components/ds2'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { showError, showSuccess } from '@/utils/snackbar'
import { useExistingInvoiceBatchSearch } from '@/pages/dashboard/pages/accounting/pages/invoices/hooks/useExistingInvoiceBatchSearch'
import { useInvoicingRowsStore } from '../../invoicing/rowsStore'
import { useInvoicingChat } from '../../invoicing/chatBridge'
import { formatCount, formatUsd } from '../results/format'
import { createdBatchNoteOf, createInvoiceBatch } from './createBatch'
import { amountOf, rowKeyOf, statusOf } from './invoicingRows'
import { PanelDrawer } from './PanelDrawer'

export type BatchMode = 'new' | 'existing'

interface CreateBatchDrawerProps {
    open: boolean
    /** Create a new batch, or add the loads to a batch already on the Invoices page. */
    mode: BatchMode
    /** The selected loads, none invoiced yet, in table order. */
    rows: TmsInvoiceRow[]
    onClose: () => void
    onRemove: (key: string) => void
    onDone: () => void
}

/** What a load still lacks, said on its row so the user batches it knowingly (the Invoices page batches it all the same). */
const gapsOf = (row: TmsInvoiceRow): string | null => {
    const gaps = [
        row.documents?.rate_con === false ? 'no rate con' : null,
        row.documents && row.documents.pod !== 'accepted' ? (row.documents.pod === 'none' ? 'no POD' : 'POD not accepted') : null,
        row.amount_due_usd == null ? 'no rate' : null,
    ].filter(Boolean)
    return gaps.length > 0 ? `Invoiced without review: ${gaps.join(', ')}` : null
}

/**
 * Create batch / Add to batch (the Invoices page's Ready to Invoice actions, in the chat): the selected loads with their amounts and
 * anything they still lack, a batch name or an existing batch, and the button that invoices them. Nothing goes to the factor from here.
 */
export const CreateBatchDrawer = ({ open, mode, rows, onClose, onRemove, onDone }: CreateBatchDrawerProps) => {
    const [name, setName] = useState('')
    const [search, setSearch] = useState('')
    const [existing, setExisting] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const chat = useInvoicingChat()
    const batches = useExistingInvoiceBatchSearch({ enabled: open && mode === 'existing', search })

    if (!open) return null

    const appending = mode === 'existing'
    const total = rows.reduce((sum, row) => sum + amountOf(row), 0)
    const withGaps = rows.filter((row) => gapsOf(row) !== null).length
    const canConfirm = rows.length > 0 && !busy && (!appending || existing !== null)

    const confirm = async () => {
        setBusy(true)
        try {
            const created = await createInvoiceBatch(rows, appending ? (existing ?? '') : name, appending)
            chat.appendNote(createdBatchNoteOf(created, appending))
            showSuccess(appending ? `Added to batch “${created.batchName}”.` : `Batch “${created.batchName}” created.`)
            onDone()
            const tabId = await chat.ensureTab()
            await useInvoicingRowsStore.getState().refresh(
                tabId,
                rows.map((row) => row.load_numbers[0]).filter((n): n is string => Boolean(n))
            )
        } catch (failure) {
            showError(getUserErrorMessage(failure))
        } finally {
            setBusy(false)
        }
    }

    return (
        <PanelDrawer
            open={open}
            onClose={onClose}
            title={appending ? 'Add to an existing batch' : 'Create batch'}
            subtitle={
                <>
                    {formatCount(rows.length)} {rows.length === 1 ? 'load' : 'loads'} · {formatUsd(total)}
                </>
            }
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>
                        Back
                    </Button>
                    {/* The label stays while it works (a loading button shrinks to its spinner): it says what is being done. */}
                    <Button onClick={() => void confirm()} disabled={!canConfirm}>
                        {busy
                            ? appending
                                ? 'Adding to batch…'
                                : 'Creating batch…'
                            : appending
                              ? 'Add to batch'
                              : `Create batch · ${formatCount(rows.length)} ${rows.length === 1 ? 'invoice' : 'invoices'}`}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4 text-sm">
                {appending ? (
                    <div className="flex flex-col gap-2">
                        <Input
                            label="Batch"
                            variant="outlined"
                            placeholder="Search batches by name"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                        <div role="listbox" aria-label="Existing batches" className="max-h-56 overflow-y-auto rounded-xl border border-border">
                            {batches.loading && <p className="px-3 py-3 text-xs text-foreground-muted">Loading batches…</p>}
                            {batches.error && <p className="px-3 py-3 text-xs text-danger">{batches.error}</p>}
                            {!batches.loading && !batches.error && batches.options.length === 0 && (
                                <p className="px-3 py-3 text-xs text-foreground-muted">No open batch to add to. Create a new batch instead.</p>
                            )}
                            {batches.options.map((option) => {
                                const picked = existing === option.batchName
                                return (
                                    <button
                                        key={option.batchName}
                                        type="button"
                                        role="option"
                                        aria-selected={picked}
                                        onClick={() => setExisting(option.batchName)}
                                        className={`flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left last:border-b-0 ${
                                            picked ? 'bg-accent-soft text-accent-soft-foreground' : 'hover:bg-default'
                                        }`}
                                    >
                                        <span className="min-w-0 truncate font-medium">{option.batchName}</span>
                                        <span className="shrink-0 text-xs tabular-nums text-foreground-muted">
                                            {formatCount(option.invoiceCount)} {option.invoiceCount === 1 ? 'invoice' : 'invoices'} ·{' '}
                                            {formatUsd(option.totalDue)}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                ) : (
                    <Input
                        label="Batch name"
                        variant="outlined"
                        placeholder="Optional: a name is made when left empty"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                    />
                )}

                <div className="flex flex-col gap-2">
                    {rows.map((row) => {
                        const gaps = gapsOf(row)
                        return (
                            <div key={rowKeyOf(row)} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium text-foreground">
                                        #{row.load_numbers[0] ?? ''} · {row.broker ?? '—'}
                                    </div>
                                    <div className={`mt-1 flex items-center gap-2 text-xs ${gaps ? 'text-warning-soft-foreground' : 'text-foreground-muted'}`}>
                                        <span>{gaps ?? (statusOf(row) === 'ready' ? 'Ready to invoice' : 'Invoice will be created')}</span>
                                        <button type="button" className="text-foreground-muted underline" onClick={() => onRemove(rowKeyOf(row))}>
                                            Remove
                                        </button>
                                    </div>
                                </div>
                                <strong className="tabular-nums">{formatUsd(amountOf(row))}</strong>
                            </div>
                        )
                    })}
                </div>

                {withGaps > 0 && (
                    <p role="status" className="rounded-lg bg-warning-soft px-3 py-2 text-xs text-warning-soft-foreground">
                        {formatCount(withGaps)} of these {withGaps === 1 ? 'load is' : 'loads are'} invoiced without a reviewed POD or rate con, as on the
                        Invoices page. Review a load first to check its paperwork.
                    </p>
                )}
                <p className="rounded-lg bg-surface-secondary px-3 py-2 text-xs text-foreground-muted">
                    One invoice is created per load. Nothing is emailed or sent to the factor from here.
                </p>
            </div>
        </PanelDrawer>
    )
}
